import React from 'react';

/**
 * Result-level filter bar (movies/series). These are HARD filters, separate from
 * the AI refinement: changing any of them re-runs the search and refreshes the
 * whole result set + the AI recommendations.
 *
 * Three controls:
 *   - Streaming service  (multi-select brand chips)
 *   - Release year       (presets — production year, NOT the depicted era)
 *   - Type               (All / Movies / Series)
 *
 * Release year is deliberately independent of the query: search "historical
 * movies" (semantic/era) and then pick "2020+" to get recently-MADE ones.
 *
 * Props:
 *   brands      — [{ key, label }]
 *   providers   — selected brand keys
 *   yearKey     — selected year preset key
 *   contentType — null | 'movie' | 'series'
 *   onChange    — ({ providers, yearKey, yearStart, yearEnd, contentType }) => void
 */
export const YEAR_PRESETS = [
  { key: 'any', label: 'Any year', start: null, end: null },
  { key: '2024', label: '2024+', start: 2024, end: null },
  { key: '2020', label: '2020+', start: 2020, end: null },
  { key: '2010s', label: '2010s', start: 2010, end: 2019 },
  { key: '2000s', label: '2000s', start: 2000, end: 2009 },
  { key: '90s', label: "'90s", start: 1990, end: 1999 },
  { key: 'older', label: 'Pre-90s', start: null, end: 1989 },
];

// Map a (start,end) pair (e.g. parsed from the query) onto a preset key.
export const yearKeyFor = (start, end) => {
  const p = YEAR_PRESETS.find((x) => x.start === (start ?? null) && x.end === (end ?? null));
  return p ? p.key : 'any';
};

const TYPES = [
  { key: null, label: 'All' },
  { key: 'movie', label: 'Movies' },
  { key: 'series', label: 'Series' },
];

const FilterBar = ({ brands = [], providers = [], yearKey = 'any', contentType = null, onChange }) => {
  const emit = (patch) => {
    const next = { providers, yearKey, contentType, ...patch };
    const preset = YEAR_PRESETS.find((p) => p.key === next.yearKey) || YEAR_PRESETS[0];
    onChange && onChange({ ...next, yearStart: preset.start, yearEnd: preset.end });
  };

  const toggleProvider = (key) => {
    const has = providers.includes(key);
    emit({ providers: has ? providers.filter((p) => p !== key) : [...providers, key] });
  };

  // Editorial chip: active = cream fill / dark ink; inactive = transparent w/ hairline.
  const pill = (selected) =>
    `px-3 py-[5px] rounded-full text-[12.5px] border whitespace-nowrap transition-all ${
      selected
        ? 'bg-binge-ink text-binge-bg border-binge-ink font-semibold'
        : 'bg-transparent text-binge-sub border-[rgba(241,236,226,0.2)] hover:border-[rgba(241,236,226,0.45)] hover:text-binge-ink'
    }`;
  const micro = 'text-[10px] uppercase tracking-[0.18em] text-binge-faint mr-1 shrink-0';

  return (
    <div className="flex items-center gap-5 py-3.5 mb-7 border-b border-[rgba(241,236,226,0.08)] flex-nowrap">
      {/* Streaming services */}
      {brands.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-0.5 min-w-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <span className={micro}>On</span>
          {brands.map((b) => (
            <button key={b.key} className={pill(providers.includes(b.key))} onClick={() => toggleProvider(b.key)}>
              {b.label}
            </button>
          ))}
        </div>
      )}

      {/* Release year */}
      <div className="flex items-center gap-2 overflow-x-auto pb-0.5 min-w-0 shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className={micro}>From</span>
        {YEAR_PRESETS.map((p) => (
          <button key={p.key} className={pill(yearKey === p.key)} onClick={() => emit({ yearKey: p.key })}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Type */}
      <div className="flex items-center gap-2 ml-auto shrink-0 pl-1">
        {TYPES.map((t) => (
          <button key={t.label} className={pill(contentType === t.key)} onClick={() => emit({ contentType: t.key })}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FilterBar;
